import React, { useState, useEffect, useCallback } from 'react';
import { graphBuilderApi } from '../../../services/api';
import './ExecutionCommon.css';

function ExecutionHistory({ processId, currentExecutionId, onLoadExecution }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Load executions
  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        process_id: processId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await graphBuilderApi.listProcessExecutions(params);
      setExecutions(response.executions || response || []);
    } catch (err) {
      console.error('Error loading executions:', err);
      setError(err.message || 'Failed to load execution history');
    } finally {
      setLoading(false);
    }
  }, [processId, statusFilter, page]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate duration
  const calculateDuration = (startedAt, completedAt) => {
    if (!startedAt) return '-';
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const duration = Math.floor((end - start) / 1000);
    
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'failed': return '✗';
      case 'cancelled': return '○';
      case 'running': return '●';
      case 'paused': return '⏸';
      default: return '○';
    }
  };

  if (loading && executions.length === 0) {
    return (
      <div className="execution-history loading">
        <div className="spinner"></div>
        <span>Loading execution history...</span>
      </div>
    );
  }

  return (
    <div className="execution-history">
      {/* Toolbar */}
      <div className="history-toolbar">
        <div className="toolbar-left">
          <h3>Execution History</h3>
          <button className="refresh-btn" onClick={loadExecutions} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
        <div className="toolbar-right">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={statusFilter} 
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="running">Running</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="history-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Executions Table */}
      {executions.length === 0 ? (
        <div className="empty-history">
          <div className="empty-icon">📋</div>
          <h4>No Executions Found</h4>
          <p>Run the process to create execution records</p>
        </div>
      ) : (
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>Execution ID</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Progress</th>
                <th>Error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((exec) => {
                const isCurrent = exec.execution_id === currentExecutionId;
                
                return (
                  <tr 
                    key={exec.execution_id} 
                    className={`${isCurrent ? 'current' : ''} ${exec.status}`}
                  >
                    <td className="exec-id">
                      <span title={exec.execution_id}>
                        {exec.execution_id.substring(0, 8)}...
                      </span>
                      {isCurrent && <span className="current-badge">Current</span>}
                    </td>
                    <td className="exec-status">
                      <span className={`status-icon ${exec.status}`}>
                        {getStatusIcon(exec.status)}
                      </span>
                      <span className={`status-text ${exec.status}`}>
                        {exec.status}
                      </span>
                    </td>
                    <td className="exec-started">
                      {formatDate(exec.started_at)}
                    </td>
                    <td className="exec-duration">
                      {calculateDuration(exec.started_at, exec.completed_at)}
                    </td>
                    <td className="exec-progress">
                      <div className="mini-progress">
                        <div 
                          className="mini-progress-bar"
                          style={{ width: `${exec.progress || 0}%` }}
                        />
                      </div>
                      <span>{(exec.progress || 0).toFixed(0)}%</span>
                    </td>
                    <td className="exec-error">
                      {exec.error ? (
                        <span className="error-preview" title={exec.error}>
                          {exec.error.substring(0, 30)}...
                        </span>
                      ) : '-'}
                    </td>
                    <td className="exec-actions">
                      <button
                        className="view-btn"
                        onClick={() => onLoadExecution(exec.execution_id)}
                      >
                        {isCurrent ? 'Reload' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {executions.length >= pageSize && (
        <div className="history-pagination">
          <button 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            ← Previous
          </button>
          <span>Page {page}</span>
          <button 
            disabled={executions.length < pageSize}
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default ExecutionHistory;
